import React, { useState } from 'react';
import './Navigation.css';
// Also the Deploy button's glyph — the destination icons live in navDestinations.
import SecuritySharpIcon from '@mui/icons-material/SecuritySharp';
import { Menu, message, Popconfirm, Button, Badge, Popover, Spin } from 'antd';
import { CloudOutlined, UserOutlined, DownOutlined, NotificationOutlined } from '@ant-design/icons';
import APIClient from '../../api.service';
import { STATUS_COLORS } from '../../styles/colors';
import { AuthenticationProvider } from '../../Services/AuthenticationProvider';
import useCourseTime from '../../hooks/useCourseTime';
import { useDashboard } from '../../Contexts/DashboardContext';
import useBreakpoint from '../../hooks/useBreakpoint';
import NAV_DESTINATIONS from './navDestinations';
import { hasSatellites } from '../../utils/capability';
import BottomTabBar from './BottomTabBar';

const Navigation = () => {
  const courseTime = useCourseTime();
  const current = window.location.pathname;
  // Phones get the bottom tab bar instead of the header's destination strip.
  const isCompact = useBreakpoint() === 'compact';
  const [isRainHoldButtonLoading, setIsRainHoldButtonLoading] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [notificationVisible, setNotificationVisible] = useState(false);

  // Use dashboard context
  const {
    rainHoldActive,
    setRainHoldActive,
    isScheduleModified,
    allNotifications,
    badgeCount,
    setBadgeCount,
    isPageLoading,
    isStale,
    isAccessDenied,
    refreshDashboard,
    satelliteCount,
  } = useDashboard();

  // The same rule the router gates on (utils/capability holds it, and the reasoning for failing
  // open on an unknown count). Shared rather than restated so the menu can never offer a screen
  // the router redirects away from, or hide one it still serves.
  const showSatelliteFeatures = hasSatellites(satelliteCount);

  const handleClick = (e) => {
    window.location.pathname = e.key;
  };

  const deploySchedule = async () => {
    try {
      const apiClient = new APIClient();
      const response = await apiClient.commitSchedule();
      if (response.type === 'success') {
        message.success('Schedule successfully deployed.');
        // Refresh dashboard status to update schedule modification flag
        refreshDashboard();
      } else {
        message.error(response.message);
      }
    } catch {
      message.error('Schedule failed to deploy.');
    }
  };

  const handleLogout = async () => {
    setIsLogoutLoading(true);
    try {
      await AuthenticationProvider.logout();
      message.success('Logged out successfully.');
    } catch (error) {
      message.error('Logout failed.');
    } finally {
      setIsLogoutLoading(false);
    }
  };

  const toggleRainHold = async () => {
    const newStatus = !rainHoldActive;
    setRainHoldActive(newStatus);
    setIsRainHoldButtonLoading(true);
    try {
      const apiClient = new APIClient();
      await apiClient.setRainHoldStatus(newStatus);
      message.success(newStatus ? 'Rain hold activated.' : 'Rain hold deactivated.');
    } catch (error) {
      setRainHoldActive(!newStatus);
      message.error('Failed to toggle rain hold.');
    } finally {
      setIsRainHoldButtonLoading(false);
    }
  };

  // Handle notification popover visibility and mark notifications as viewed
  const handleNotificationOpenChange = async (visible) => {
    setNotificationVisible(visible);

    if (visible && badgeCount > 0) {
      setBadgeCount(0); // Immediate reset
      try {
        const apiClient = new APIClient();
        await apiClient.perform('post', '/dashboard_status', { mark_viewed: true });
      } catch (e) {
        // Fire-and-forget: the badge is already cleared locally and the next
        // dashboard_status poll re-derives the real count.
      }
    }
  };

  // Notification popover content with error stations list
  const notificationContent = (
    <div style={{ width: 280, maxHeight: 400, overflow: 'auto' }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        marginBottom: 8,
        fontWeight: 'bold',
        fontSize: '14px'
      }}>
        Notification List
      </div>

      {allNotifications.length === 0 ? (
        <div style={{
          padding: '16px',
          textAlign: 'center',
          color: 'var(--muted)'
        }}>
          No notifications
        </div>
      ) : (
        <div>
          {allNotifications.slice(0, 5).map((notification, index) => (
            <div
              key={`${notification.satelliteID}-${notification.stationID}`}
              style={{
                padding: '12px 16px',
                borderBottom: index < Math.min(allNotifications.length, 5) - 1 ? '1px solid #f5f5f5' : 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              className="notification-item"
              onClick={() => {
                setNotificationVisible(false);
                window.location.href = '/Schedule/List';
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{
                fontSize: '13px',
                fontWeight: 500,
                marginBottom: '4px'
              }}>
                Satellite {notification.satelliteID} - Station {notification.stationID}
              </div>
              <div style={{
                fontSize: '12px',
                color: STATUS_COLORS.fault,
                marginBottom: '4px'
              }}>
                Hardware Issue/No Communication
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--muted)'
              }}>
                {notification.error_started_at
                  ? `Issue detected at ${courseTime.display(notification.error_started_at, 'MMM D, h:mm A')}`
                  : 'Recently'
                }
              </div>
            </div>
          ))}
          {/* Gated like the destinations above: this navigates to /Schedule/List, which is not
              in the menu on a tenant with no satellites and whose route redirects to Home. */}
          {showSatelliteFeatures && allNotifications.length > 5 && (
            <div style={{
              padding: '12px 16px',
              textAlign: 'center',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fafafa'
            }}>
              <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                +{allNotifications.length - 5} more notifications
              </span>
              <br />
              <Button
                type="link"
                size="small"
                style={{ padding: '4px 0', fontSize: '12px' }}
                onClick={() => {
                  setNotificationVisible(false);
                  window.location.href = '/Schedule/List';
                }}
              >
                View All Error Stations
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Determine the button class based on the rain hold state
  const buttonClass = rainHoldActive ? 'rain-hold-active' : 'rain-hold';
  // Zero satellites removes Schedule and Map here and redirects their routes in App.js, so the
  // link and the URL agree. Both nav surfaces (header menu and <BottomTabBar>) render from this
  // one list so they cannot drift.
  const visibleDestinations = NAV_DESTINATIONS.filter(
    ({ requiresSatellites }) => showSatelliteFeatures || !requiresSatellites
  );

  // The header menu — tablet and desktop only; phones get <BottomTabBar>.
  // Label text lives in an explicit <span className="menu-label"> so it can be
  // targeted on its own: a bare text node (what this used to be) is invisible to
  // CSS, which is why the old `.menu-item span:not(.menu-icon)` rule matched
  // nothing — the only child element is the <svg> icon.
  const mainNavItems = visibleDestinations.map(({ key, label, Icon, children }) => ({
    key,
    label: (
      // 640-1023px hides .menu-label (Navigation.css), leaving a bare glyph with
      // no way for a mouse user to learn what it is. The native tooltip is the
      // affordance; it repeats the label exactly, so it adds nothing at the
      // widths where the label is already on screen.
      <span className="menu-item" title={label}>
        <Icon fontSize="small" className="menu-icon" />
        <span className="menu-label">{label}</span>
      </span>
    ),
    ...(children ? { children: children.map((c) => ({ key: c.key, label: c.label })) } : {}),
  }));

  return (
    <>
    <div className="header-container">
      {/* At compact the destinations move to <BottomTabBar>, so the header keeps
          a single row of actions. Conditional render, not display:none: two live
          copies of the same six links would be duplicated for assistive tech. */}
      {!isCompact && (
        <Menu
          defaultOpenKeys={[current]}
          selectedKeys={[current]}
          mode="horizontal"
          theme="dark"
          onClick={handleClick}
          items={mainNavItems}
          className="main-menu"
          disabledOverflow
        />
      )}

      <div className="button-container">
        {/* Distinct copy per failure kind: "Reconnecting" implies a reload will help, which
            is false for an authorization failure. */}
        {isAccessDenied ? (
          <span
            data-testid="connection-denied"
            title="This account does not have permission to read the dashboard status, so deploy state and rain hold may not work. Your schedules keep running normally — please contact support."
            style={{ color: STATUS_COLORS.fault, fontSize: '12px', marginRight: '8px', alignSelf: 'center' }}
          >
            ⚠ Data unavailable
          </span>
        ) : isStale ? (
          <span
            data-testid="connection-stale"
            title="Reconnecting to the server…"
            style={{ color: STATUS_COLORS.warn, fontSize: '12px', marginRight: '8px', alignSelf: 'center' }}
          >
            ⚠ Reconnecting…
          </span>
        ) : null}
        {/* Rain hold stops — or resumes — watering for the whole course, and there
            is no undo, so the tap only opens a confirmation; toggleRainHold runs
            from onConfirm. Same disabled-child caveat as the deploy button below:
            while loading, render the bare button so antd's compat <span> can't
            take .rain-hold/.rain-hold-active (colour + the 44px phone height)
            off the real <button>. */}
        {/* Rain hold and deploy act on satellites, so a zero-satellite tenant gets neither
            button. Same fail-open rule as the menu: an unknown count keeps them visible. */}
        {showSatelliteFeatures && (() => {
          const rainHoldButton = (
            <Button
              data-testid="rain-hold-button"
              type="primary"
              icon={<CloudOutlined />}
              className={buttonClass}
              loading={isRainHoldButtonLoading}
              disabled={isPageLoading}
              style={{
                backgroundColor: rainHoldActive ? STATUS_COLORS.fault : 'var(--brand)',
                borderColor: rainHoldActive ? STATUS_COLORS.fault : 'var(--brand)'
              }}
            >
              {rainHoldActive ? 'Resume Schedule' : 'Rain Hold'}
            </Button>
          );
          return isPageLoading ? (
            rainHoldButton
          ) : (
            <Popconfirm
              title={
                rainHoldActive
                  ? 'Are you sure you want to turn off Rain Hold?'
                  : 'Are you sure you want to turn on Rain Hold?'
              }
              onConfirm={toggleRainHold}
              okText={rainHoldActive ? 'Turn Off' : 'Turn On'}
              cancelText="Cancel"
              // Bottom-anchored: the header sits at the top of the viewport, so the
              // default 'top' placement would open off-screen on a phone. Right-aligned
              // so a long prompt grows inwards instead of past the right edge at 360px.
              placement="bottomRight"
            >
              {rainHoldButton}
            </Popconfirm>
          );
        })()}

        {/* While loading, render the disabled button without the Popconfirm wrapper:
            antd moves a disabled child's className onto a compat <span>, which would
            detach the .deploy-button styles from the actual button.
            Focus-safety of this swap (and the bell/Spin swap below) relies on
            usePolledResource's isLoading being one-shot — it never resets to true —
            so revisit both ternaries if a "re-loading" state is ever introduced. */}
        {showSatelliteFeatures && (() => {
          const deployButton = (
            <Button
              type="primary"
              disabled={isPageLoading}
              className={`deploy-button ${isScheduleModified ? 'deploy-button-changes' : ''}`}
              style={{
                backgroundColor: isScheduleModified ? STATUS_COLORS.run : '#1f1f1f',
                borderColor: isScheduleModified ? STATUS_COLORS.run : '#1f1f1f',
                color: 'white'
              }}
            >
    <span className="menu-item">
      <SecuritySharpIcon fontSize="small" className="menu-icon" />
      {isScheduleModified ? 'Deploy New Changes' : 'Deploy Schedule'}
    </span>
            </Button>
          );
          return isPageLoading ? (
            deployButton
          ) : (
            <Popconfirm
              title="Are you sure you want to deploy the schedule?"
              description="Confirm deploy schedule"
              onConfirm={deploySchedule}
              okText="Deploy"
              cancelText="Cancel"
            >
              {deployButton}
            </Popconfirm>
          );
        })()}

        {/* Notification Bell with badge count and pop over.
            While the first dashboard status is loading, show a spinner instead.
            Not capability-gated: notifications derive from stations, so a zero-satellite
            tenant simply never has any - and its "View All Error Stations" link (which targets
            the hidden /Schedule/List) is therefore unreachable there. */}
        {isPageLoading ? (
          <Spin size="small" style={{ margin: '0 12px' }} />
        ) : (
          <Popover
            content={notificationContent}
            title={null}
            trigger="click"
            open={notificationVisible}
            onOpenChange={handleNotificationOpenChange}
            placement="bottomRight"
          >
            <Badge count={badgeCount} size="small">
              <Button
                icon={<NotificationOutlined />}
                type="text"
                // 44px hit area without growing the glyph box — see Navigation.css.
                className="notification-bell touch-target"
                // Notification bell click and reset badge count
                onClick={async () => {
                  setNotificationVisible(true);
                  if (badgeCount > 0) {
                    setBadgeCount(0); // Immediate badge reset
                    try {
                      const apiClient = new APIClient();
                      await apiClient.perform('post', '/dashboard_status', { mark_viewed: true });
                    } catch (e) {
                      // Fire-and-forget — see handleNotificationOpenChange.
                    }
                  }
                }}
              />
            </Badge>
          </Popover>
        )}

        <Popconfirm
          title="Are you sure you want to logout?"
          description="You will be redirected to the login page"
          onConfirm={handleLogout}
          okText="Logout"
          cancelText="Cancel"
        >
          <Button
            // 44px tall from CSS on phones; touch-target holds the hit area at
            // 44 on the tablet/desktop tiers, where the box stays 26-28px.
            className="user-dropdown-button touch-target"
            loading={isLogoutLoading}
          >
            <UserOutlined className="user-icon" />
            <DownOutlined className="dropdown-arrow" />
          </Button>
        </Popconfirm>
      </div>
    </div>
    {isCompact && (
      <BottomTabBar
        pathname={current}
        onNavigate={(key) => handleClick({ key })}
        destinations={visibleDestinations}
      />
    )}
    </>
  );
};

export default Navigation;